import { createSelector, createFeatureSelector } from "@ngrx/store";
import { fhirState } from "../reducers/fhir.reducer";
import { SubTaskObject, FlattenedFhirState } from "../../models/types";
import { codingMatch } from "../../utils/coding-match";
/// <reference types="fhir" />

export const getFhirState = createFeatureSelector<fhirState>("fhir");

export const getTasks = createSelector(getFhirState, (resources: fhirState) => {
  return resources.filter(resource => {
    return resource.resourceType === "Task";
  }) as fhir.Task[];
});

export const getPromParentTask = createSelector(getTasks, taskResources => {
  const parentTask = taskResources.find(resource =>
    codingMatch(resource.code.coding, [
      { code: "parentTask", present: true },
      { code: "consentTask", present: false }
    ])
  );
  return parentTask !== undefined ? parentTask : null;
});

export const getPromSubTasks = createSelector(getTasks, taskResources => {
  return taskResources.filter(resource =>
    codingMatch(resource.code.coding, [
      { code: "subTask", present: true },
      { code: "consentTask", present: false }
    ])
  );
});

export const getAllSubTasks = createSelector(getTasks, taskResources => {
  return taskResources.filter(resource =>
    codingMatch(resource.code.coding, [
      { code: "subTask", present: true }
    ])
  );
});

export const getConsentParentTask = createSelector(getTasks, taskResources => {
  const parentTask = taskResources.find(resource =>
    codingMatch(resource.code.coding, [
      { code: "parentTask", present: true },
      { code: "consentTask", present: true }
    ])
  );
  return parentTask !== undefined ? parentTask : null;
});

export const getConsentSubTasks = createSelector(getTasks, taskResources => {
  return taskResources.filter(resource =>
    codingMatch(resource.code.coding, [
      { code: "subTask", present: true },
      { code: "consentTask", present: true }
    ])
  );
});

export const getProcedureRequests = createSelector(
  getFhirState,
  (resources: fhirState) => {
    return resources.filter(resource => {
      return resource.resourceType === "ProcedureRequest";
    }) as fhir.ProcedureRequest[];
  }
);

export const getQuestionnaireResponses = createSelector(
  getFhirState,
  (resources: fhirState) => {
    return resources.filter(resource => {
      return resource.resourceType === "QuestionnaireResponse";
    }) as fhir.QuestionnaireResponse[];
  }
);

export const getQuestionnaires = createSelector(
  getFhirState,
  (resources: fhirState) => {
    return resources.filter(resource => {
      return resource.resourceType === "Questionnaire";
    }) as fhir.Questionnaire[];
  }
);

export const getPatients = createSelector(
  getFhirState,
  (resources: fhirState) => {
    return resources.filter(resource => {
      return resource.resourceType === "Patient";
    }) as fhir.Patient[];
  }
);

export const getFlattenedFhir = createSelector(
  getAllSubTasks,
  getProcedureRequests,
  getQuestionnaires,
  (subTasks, procedureRequests, questionnaires) => {
    const flattenedFhir = subTasks.reduce((accumulator, task) => {
      let procedureRequest = procedureRequests.find(
        request =>
          `ProcedureRequest/${request.id}` === task.basedOn[0].reference
      );
      if (procedureRequest === undefined) {
        procedureRequest = null;
      }
      let questionnaire =
        procedureRequest === null
          ? null
          : questionnaires.find(
              questionnnaire =>
                `Questionnaire/${questionnnaire.id}` ===
                procedureRequest.definition[0].reference
            );
      if (questionnaire === undefined) {
        questionnaire = null;
      }
      const subTaskObj: SubTaskObject = {
        task,
        procedureRequest,
        questionnaire,
        questionnaireResponse: null
      };
      return { ...accumulator, [task.id]: subTaskObj };
    }, {}) as FlattenedFhirState;
    return flattenedFhir;
  }
);



// WEBPACK FOOTER //
// ./src/app/store/selectors/fhir.selectors.ts